import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

type Point = { t: number; v: number };

export type Series = {
  name: string;
  points: Point[];
  color?: string;
};

function fmtTime(tsMs: number) {
  try {
    return new Date(tsMs).toLocaleTimeString();
  } catch {
    return String(tsMs);
  }
}

function fmtNum(v: any) {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  if (Math.abs(n) >= 1000) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(2);
  return n.toFixed(4);
}

const DEFAULT_PALETTE = [
  "#2563eb", // blue
  "#16a34a", // green
  "#f59e0b", // amber
  "#dc2626", // red
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#db2777", // pink
  "#65a30d", // lime
  "#ea580c", // orange
  "#64748b", // slate
];

export default function TimeSeriesChart(props: {
  title: string;
  series: Series[];
  height?: number;
}) {
  const height = props.height ?? 320;

  // Build a single data array: [{t: <ms>, "<seriesName>": v, ...}, ...]
  const data = useMemo(() => {
    const map = new Map<number, Record<string, any>>();
    for (const s of props.series || []) {
      for (const p of s.points || []) {
        const row = map.get(p.t) || { t: p.t };
        row[s.name] = p.v;
        map.set(p.t, row);
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.t as number) - (b.t as number));
  }, [props.series]);

  const lines = useMemo(() => {
    return (props.series || []).map((s, idx) => {
      const stroke = s.color || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
      return (
        <Line
          key={s.name}
          type="monotone"
          dataKey={s.name}
          dot={false}
          strokeWidth={2}
          stroke={stroke}
          isAnimationActive={false}
          connectNulls
        />
      );
    });
  }, [props.series]);

  return (
    <div className="border rounded bg-white p-4">
      <div className="text-sm font-medium">{props.title}</div>
      <div className="mt-3" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              tickFormatter={(v) => fmtTime(Number(v))}
              minTickGap={24}
            />
            <YAxis tickFormatter={(v) => fmtNum(v)} width={56} />
            <Tooltip
              labelFormatter={(v) => fmtTime(Number(v))}
              formatter={(v: any) => fmtNum(v)}
            />
            <Legend />
            {lines}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}